<template lang="pug">
	.audio-tracker-insertion--voices-selector.flex-col
		.flex-row.align-center.space
			DxSelectBox.flex-1(
				:dataSource="source",
				:placeholder="$tCaption('Select voice')",
				:value="selectedVoice && selectedVoice.uniqueId",
				value-expr="uniqueId",
				display-expr="text",
				:searchEnabled="true"
				:showClearButton="true",
				:buttons="dropDownButtons",
				:noDataText="`${searchValue || ''}`.length < minSearchLength ? $tCaption('Please enter minimum <NUM> characters').replace('<NUM>', minSearchLength) : $tCaption('No data', '@actions')",
				:validationStatus="!selectedVoice ? 'invalid' : 'valid'",
				@selection-changed="onSelectionChanged",
				@item-click="onItemClick",
				:disabled="initiatingVoice"
			)
			Loader(v-if="initiatingVoice", text="")
		Popup(:v-if="opened", :popupVisible="opened", :width="920", :height="540", :autosize="true")
			template(v-if="page === 'voice' && detailVoice")
				VoiceDetails(
					:creativeIabCategories="creativeIabCategories",
					:voice.sync="detailVoice",
					:audioContext="audioContext"
					@isLoadingVoiceDetails="(v) => {isLoading = v}"
					@getMatchedIabCategories="getMatchedIabCategories"
					@getRestrictedIabCategories="getRestrictedIabCategories",
					@voice-details="v => selectedVoiceDetails = v"
				)
					.clickable.grey.flex-row.space.align-center(slot="top", @click="page = 'list'")
						.icon
							i.fas.fa-chevron-left
						span {{$tCaption('Back to voices list')}}
			.flex-row.space-large.audio-tracker-insertion--voices-selector(v-else)
				.flex-col.space-large
					.filters.flex-col.space-large.flex-1
						.flex-col.field
							.font-small.upper.grey {{$tCaption('Search')}}
							DxTextBox(:value.sync="filters.value.name", placeholder="", valueChangeEvent="input", :showClearButton="true")
						.flex-col.field
							.flex-row.space.align-center.grey
								.font-small.upper {{$tCaption('Language')}}
								fas(v-if="!allVoicesLoaded" icon="spinner" :spin="true")
							DxSelectBox(:dataSource="getStore(languages)", keyExpr="id", displayExpr="text", :searchEnabled="true", :value.sync="filters.value.language", placeholder="", :showClearButton="true", :disabled="!allVoicesLoaded")
						.flex-col.field
							.font-small.upper.grey {{$tCaption('Gender')}}
							DxSelectBox(:dataSource="getStore(genders)", keyExpr="id", displayExpr="text", :searchEnabled="true", :value.sync="filters.value.gender", placeholder="", :showClearButton="true")
						.flex-col.field
							.font-small.upper.grey {{$tCaption('Voice type')}}
							DxSelectBox(:dataSource="getStore(voiceTypes)", keyExpr="id", displayExpr="text", :searchEnabled="true", :value.sync="filters.value.voiceType", placeholder="", :showClearButton="true")
						.flex-col.field
							.font-small.upper.grey {{$tCaption('Category')}}
							DxSelectBox(:dataSource="getStore(categories)", keyExpr="id", displayExpr="text", :searchEnabled="true", :value.sync="filters.value.category", placeholder="", :showClearButton="true")
						.flex-col.field
							.font-small.upper.grey {{$tCaption('Provider')}}
							DxSelectBox(:dataSource="getStore(providerTypes)", keyExpr="id", displayExpr="name", :searchEnabled="false", :value="filters.value.providerType", placeholder="", :showClearButton="true",
								@item-click="onProvideTypeChange", @value-changed="onProvideTypeValueChange")
				ListView.flex-3(ref="voicesListView", :getTotal="(r) => r.total", :getItems="(r) => r.results", :getData="getData", :bottomPagerVisible="false",
					initialView="cards", :height="400", :hideViewsSelection="true", :pageSize="6", :currentPage.sync="currentPage")
					template(v-slot:card="{ data }")
						.audio-tracker-insertion--voices-selector--card()
							.flex-row.justify.voice-card.clickable.align-center.space-medium(@click="onVoiceClick(data)",
								:class="[data.voice.Gender === 'Female' ? 'female' : 'male', data.isCustom ? 'custom-voice' : '', selectedVoice === data ? 'selected' : '']")
								.flex-row.space-medium.align-center
									.voice-image(v-if="data.voice.ImageUrl", :style="{ '--image': `url('${data.voice.ImageUrl}')` }")
									.voice-male.align-center.center.flex-row(v-else)
										.male(v-show="!data.voice.ImageUrl && data.voice.Gender === 'Male'")
											i.fas.fa-male.gender-icon
										.female(v-show="!data.voice.ImageUrl && data.voice.Gender === 'Female'")
											i.fas.fa-female.gender-icon
									.flex-col
										.flex-row.align-center.space
											.voice-name {{data.displayName}}
										.flex-row.space
											.language.font-tiny.grey {{data.language && data.language.text}}, {{data.voice.Gender}}
								.ui-button.scale.clickable.preview.align-center.center.flex-row(v-if="data.voice.VoiceDemos && data.voice.VoiceDemos.length", :title="$tCaption('Listen preview')", @click.stop="isPlayingId !== data.uniqueId ? listenPreview(data) : stopListenPreview()")
									fas(:icon="isPlayingId === data.uniqueId ? 'stop' : 'volume-up'")
							.flag-holder
								.country-flag
									.flag(:style="{ '--flagUrl': `url('https://www.flagsapi.com/${data.language && data.language.countryCode}/flat/64.png')` }")
							.custom-badge(v-if="data.isCustom") {{$tCaption('Premium')}}
							.right-bottom-panel.flex-row.align-center.space-small
								.hd-icon.align-center.center.flex-row(v-if="data.voice.VoiceType === 21", :title="$tCaption('HD Voice')")
									span HD
								.provider-type-icon.flex-row.align-center.center(:style="{ '--color': ['#fff', '#fff', '#000', '#0072ff'][data.voice.TtsProviderType], '--bg': ['#33ace5', '#0ea47e', '#e1e1e1', '#e3f2ff'][data.voice.TtsProviderType] }",
									:title="data.voiceType && data.voiceType.name")
									AzureIcon(v-if="data.voice.TtsProviderType === 0")
									OpenAIIcon(v-else-if="data.voice.TtsProviderType === 1")
									fas(icon="grip-lines-vertical", v-else-if="data.voice.TtsProviderType === 2")
									fas(icon="fa-brands fa-google", v-else-if="data.voice.TtsProviderType === 3")
									fas(v-else :icon="getProvTypeLetter(data)")
			template(slot="actions-right")
				DxButton(
					v-if="needToUpdateIabCategories && !isLoading"
					type="danger",
					:text="$tCaption('Update Iab Categories', '@actions')",
					:disabled="isLoading",
					@click="updateIabCategories"
				)
				DxButton(
					v-if="page === 'voice'",
					:type="isVoiceSelected(detailVoice) ? 'default' : 'success'",
					:icon="isVoiceSelected(detailVoice) ? 'close' : 'check'"
					:text="isVoiceSelected(detailVoice) ? $tCaption('Remove') : $tCaption('Select voice')",
					:disabled="limitedByIabCategories || isLoading",
					@click="toggleVoiceSelected(detailVoice)"
				)
				DxButton(
					:text="$tCaption('Cancel', '@actions')",
					@click="onCancel"
				)
			template(slot="actions-left")
				.flex-row.space-medium.align-center.grey.font-small(v-if="page === 'list'")
					span(v-if="filteredVoices") {{filteredVoices}} {{$tCaption('voices')}}
				DxButton(v-if="page === 'voice'", :text="$tCaption('Back', '@actions')", @click="page = 'list'")
		.voice-resuming(v-if="voicesLoadingState === 0") {{$tCaption('Loading voices for the current language.')}}
		.voice-resuming(v-else-if="selectedVoice && selectedVoice.isResuming") {{$tCaption('We are preparing the Premium voice you have selected. Until the voice comes online, you are using a default voice for your convenience.')}}
		.voice-resuming(v-else-if="selectedVoice && selectedVoice.isActive === false") {{$tCaption('The voice you have selected is in sleep mode.')}}
</template>

<script>
	import _ from 'lodash';
	import $ from 'jquery';
	import cognitiveArrays from './cognitive-arrays';
	import Dropdown from 'app-core/Dropdown.vue';
	import ListView from 'app-core/ListView.vue';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import Loader from 'app-core/Loader.vue';
	import VoiceDetails from './VoiceDetails.vue';
	import ArrayStore from 'devextreme/data/array_store';
	import Popup from 'app-core/Popup.vue';
	import DataSource from 'devextreme/data/data_source';
	import { SPEECH_PROVIDER_TYPES } from 'app-core/constants';
	import AzureIcon from 'assets/azure.svg';
	import OpenAIIcon from 'assets/openai.svg';

	export default {
		translationCategory: 'insertion',
		name: 'audio-tracker-insertion--voices-selector',
		components: {Dropdown, ListView, DxSelectBox, DxScrollView, DxTextBox, Loader, VoiceDetails, DxButton, Popup, AzureIcon, OpenAIIcon},
		mixins: [cognitiveArrays],
		props: ['voice', 'language', 'audioContext', 'creativeIabCategories', 'filter'],
		data() {
			return {
				selectedVoice: null,
				selectedVoiceDetails: null,
				detailVoice: null,
				filters: this.$storage.prop('voices-selector-filters-3', void 0, {
					name: null,
					language: null,
					gender: null,
					voiceType: null,
					category: null,
					providerType: null
				}),
				voiceTypes: ['Premium', 'Public'].map(f => ({ id: f, text: this.$tCaption(f) })),
				genders: ['Male', 'Female'].map(f => ({ id: f, text: this.$tCaption(f) })),
				categories: [],
				isLoading: false,
				isPlaying: false,
				isPlayingId: null,
				page: this.voice ? 'voice' : 'list',
				initiated: false,
				opened: false,
				source: new DataSource({
					paginate: true,
					pageSize: 10,
					load: (opts) => {
						if (__DEBUG__)
							console.debug('Search', { opts });
						this.searchValue = opts.searchValue;

						if (this.minSearchLength && `${this.searchValue || ''}`.length < this.minSearchLength) {
							return Promise.resolve({ data: [] });
						} else {
							let items = this.voices
								.filter(f => !this.filter || this.filter(f))
								.filter(f => this.isMatchingString(`${this.searchValue}`, `${f.text}`));
							if (__DEBUG__)
								console.debug('Items', { items, voices: this.voices });
							return Promise.resolve({ data: items.slice(opts.skip, opts.skip + opts.take) });
						}
					},
					byKey: (key) => {
						return Promise.resolve(this.voices.filter(f => f.uniqueId === key)[0]);
					},
					key: 'uniqueid'
				}),
				searchValue: null,
				minSearchLength: 2,
				dropDownButtons: ['clear', {
					location: 'after',
					name: 'list',
					options: {
						onClick: () => this.openDialog(),
						icon: 'menu'
					}
				}],
				voiceRestrictedIabCategories: [],
				matchedIabCategories: [],
				providerTypes: SPEECH_PROVIDER_TYPES.toLocale('name', this),
				totalVoices: 0,
				filteredVoices: 0,
				currentPage: 0
			}
		},
		computed: {
			selectedCategory() {
				return this.filters.value?.category?.id;
			},
			needToUpdateIabCategories() {
				return this.voiceRestrictedIabCategories.length > 0 && this.creativeIabCategories.length === 0;
			},
			limitedByIabCategories() {
				return this.matchedIabCategories.length > 0
					|| (this.voiceRestrictedIabCategories.length > 0 && this.creativeIabCategories.length === 0);
			},
			initiatingVoice() {
				return (this.voice && !this.selectedVoice) || this.voicesLoadingState === 0;
			}
		},
		mounted() {
			if (__DEBUG__)
				window.voiceSelector = this;
			if (this.voice)
				this.initVoice();
			else
				this.loadSpeechVoices(void 0, this.language);
			this.initiated = true;
		},
		watch: {
			'filters.value': {
				handler() {
					if (this.$refs.voicesListView)
						this.$refs.voicesListView.reload({ clear: true });
				},
				deep: true
			},
			selectedVoice(v) {
				if (__DEBUG__)
					console.debug('Voice changed', v);

				if (!v)
					this.page = 'list';
				else
					this.page = 'voice';
				this.$emit('selected-voice', v);
			},
			voice() {
				this.initVoice();
			},
			voices() {
				this.initVoice();
			}
		},
		methods: {
			getProvTypeLetter(voice) {
				return (voice?.voiceType?.code || '')[0]?.toLowerCase();
			},
			onProvideTypeChange(e) {
				if (__DEBUG__)
					console.debug('onProvideTypeChange', { e });
				this.filters.value.providerType = e?.itemData ? e?.itemData?.id : null;
			},
			onProvideTypeValueChange(e) {
				if (e?.value === null) {
					this.filters.value.providerType = null;
				}
			},
			onCancel() {
				this.opened = false;
			},
			openDialog() {
				this.opened = false;
				this.page = 'list';
				setImmediate(() => {
					this.opened = true;
				});
			},
			onSelectionChanged(v) {
				this.selectedVoice = v?.selectedItem;
			},
			onItemClick(e) {
				this.$emit('change', e.itemData);
			},
			getStore(items) {
				return new ArrayStore({
					data: items,
					key: 'id'
				});
			},
			isVoiceSelected(voice) {
				return this.voice && voice && voice?.id === this.voice && voice?.language?.locale === this.getLanguage(this.language);
			},
			isMatchingString(source, searchIn) {
				if (!source || !searchIn) return false;
				let hasNoMatches = source.toLowerCase().split(' ').some(m => searchIn.toLowerCase().indexOf(m) === -1);
				return !hasNoMatches;
			},
			initVoice() {
				if (this.voice) {
					this.loadSpeechVoices(void 0, this.language).then(() => {
						this.selectedVoice = this.findVoice(this.voice, this.language);
						this.activateVoice(this.selectedVoice, true);
						if (!this.categories?.length)
							this.categories = _.uniqBy(this.voices.map(f => ({ id: f.voice?.Category?.Oid, text: f.voice?.Category?.Name})).filter(f => f?.text), v => v?.id);
					});
				} else {
					this.selectedVoice = null;
				}
			},
			getWidth() {
				return `${$(this.$parent?.$el).width() || 320}px`;
			},
			getHeight() {
				return `${$(this.$parent?.$el).height() || 280}px`;
			},
			getData(pageIndex, limit, opts) {
				if (__DEBUG__)
					console.debug('Get voices data', { pageIndex, limit, opts });
				if (opts.byKey)
					return this.loadSpeechVoices(void 0, this.language).then(r => {
						return { results: r.filter(f => f.uniqueId === opts.byKey) };
					});
				return this.loadSpeechVoices(void 0, this.language).then(r => {
					if (__DEBUG__)
						console.debug('Voices loaded', r);
					let prefilteredVoices = r
						.filter(f => !this.filter || this.filter(f))
						.filter(f => `${f.voice.Status}`.toLowerCase() !== 'deprecated');
					
					let data = prefilteredVoices
						.filter(f =>
							(!this.filters.value.name
								|| f.displayName.toLowerCase().includes(this.filters.value.name.toLowerCase())
								|| f.language?.text.toLowerCase().includes(this.filters.value.name.toLowerCase())) &&
							(!this.filters.value.language || f.language?.id === this.filters.value.language.id) &&
							(!this.filters.value.gender || f.voice.Gender === this.filters.value.gender?.id) &&
							(!this.filters.value.voiceType || f.isCustom === (this.filters.value.voiceType?.id === 'Premium')) &&
							(!this.filters.value.category?.id || f.voice?.Category?.Oid === this.filters.value.category?.id) &&
							(this.filters.value.providerType === null || f.voice?.TtsProviderType === this.filters.value.providerType));

					this.totalVoices = prefilteredVoices.length;
					this.filteredVoices = data.length;

					return {
						total: data.length,
						results: data.slice(pageIndex * limit, (pageIndex + 1) * limit)
					}
				});
			},
			onVoiceClick(voice) {
				if (__DEBUG__)
					console.debug('On voice click', { voice });
				this.detailVoice = voice;
				this.page = 'voice';
			},
			async listenPreview(item) {
				if (__DEBUG__)
					console.debug('Listen preview', item);
				const preview = item.voice.VoiceDemos[0].AudioFile;
				this.stopListenPreview();
				this.isPlayingId = item.uniqueId;
				this.playingAudio = new Audio(preview.AzureFileUrl);
				this.playingAudio.play();
				this.playingAudio.onpause = () => {
					if (this.isPlayingId === item.uniqueId) {
						this.isPlayingId = null;
					}
				}
			},
			stopListenPreview() {
				if (this.playingAudio)
					this.playingAudio.pause();
				this.isPlayingId = null;
			},
			toggleVoiceSelected(voice) {
				if (__DEBUG__)
					console.debug('Toggle voice selected', { voice });
				if (this.isVoiceSelected(voice)) {
					this.$emit('change', null, null);
					this.selectedVoice = null;
				}
				else {
					this.$emit('change', voice, this.selectedVoiceDetails);
					this.selectedVoice = voice;
					this.opened = false;
				}

			},

			getRestrictedIabCategories(v) {
				this.voiceRestrictedIabCategories = v || [];
				this.$emit('getRestrictedIabCategories', v);
			},
			getMatchedIabCategories(v) {
				this.matchedIabCategories = v || [];
				this.$emit('getMatchedIabCategories', v);
			},
			updateIabCategories() {
				this.$eventsBus.$emit('update-iab-categories');
			},
		}
	};
</script>

<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.audio-tracker-insertion--voices-selector {
		.filters {
			padding: 10px 40px 20px 10px;
			border-right: 1px solid rgba(0,0,0,0.2);
		}

		.voice-resuming {
			color: #f27649;
			font-size: 10px;
			text-transform: uppercase;
		}
	}

	.audio-tracker-insertion--voices-selector--card {
		position: relative;
		transform: scale(1);
		transition: 0.3s;
		min-width: 180px;
		max-height: 120px;

		.flag-holder {
			position: absolute;
			left: 4px;
			top: 4px;
			overflow: hidden;
			width: 40px;
			height: 25px;
			opacity: 0.6;

			.country-flag {
				position: absolute;
				left: -10px;
				top: -12px;
				overflow: hidden;
				width: 20px;
				height: 35px;
				transform: rotate(45deg);

				.flag {
					background: var(--flagUrl);
					background-size: 80%;
					background-position: center;
					width: 200%;
					height: 80%;
					transform: rotate(-45deg)
				}
			}
		}

		&:hover {
			transform: scale(1.05);
		}

		.custom-badge {
			position: absolute;
			right: -6px;
			top: -6px;
			padding: 4px;
			font-size: 10px;
			background: #ff946d;
			color: $base-bg;
			border-radius: 2px;
		}

		.provider-type-icon {
			width: 18px;
			height: 18px;
			opacity: 0.5;
			font-size: 10px;
			background: var(--bg);
			border-radius: 50%;
			color: var(--color);
			overflow: hidden;
		}

		.right-bottom-panel {
			position: absolute;
			right: 4px;
			bottom: 4px;
		}

		.hd-icon {
			font-weight: 600;
			font-size: 9px;
			border-radius: 50%;
			background: #dedede;
			width: 20px;
			height: 20px;
			opacity: 0.5;
			overflow: hidden;
		}

		.voice-card {
			background: #f8f8f8;
			border: 2px solid #f6f6f6;
			min-height: 100px;
			transition: 0.3s;
			user-select: none;
			padding: 2px 10px 2px 2px;
			position: relative;
			border-radius: 2px;

			&.selected {
				background: #fafafa;
				border: 2px solid $base-accent;
			}

			&:hover {
				background: #fafafa;
				border: 2px solid $base-accent-transparent;
			}

			.voice-image {
				background: var(--image);
				min-width: 80px;
				min-height: 80px;
				max-width: 80px;
				max-height: 80px;
				background-size: cover;
				background-position: center;
				margin: 4px;
				border-radius: 50%;
				border: 2px solid rgb(241,241,241);
			}

			.voice-male {
				min-width: 50px;
				min-height: 50px;
				margin: 4px;
			}

			.gender-icon {
				font-size: 40px;
			}

			&.male {
				.gender-icon {
					color: #8ed8cf;
				}
			}

			&.female {
				.gender-icon {
					color: #ffcdad;
				}
			}

			.preview {
				color: #000;
			}

			.neural {
				text-transform: uppercase;
				font-size: 10px;
				font-weight: 100;
				padding: 4px;
				background: rgba(0,0,0,0.05);
				border-radius: 2px;
				margin: 2px 0px;
			}

			.voice-name {
				font-weight: 100;
				font-size: 16px;
			}

			&.custom-voice {
				background: radial-gradient(circle, rgb(242 118 73) 0%, rgb(252 70 70) 100%);

				&.male, &.female {
					.gender-icon {
						color: #ffffff90;
					}
				}

				.language {
					color: rgba(255,255,255,0.6);
				}

				.voice-name {
					color: rgba(255,255,255,0.8);
				}
			}
		}
	}
</style>
